import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import useModal from '../../../../hooks/useModal';

import { Modal, Backdrop, Fade } from '@material-ui/core';
import color from '../../../../assets/css/colors';
import fontFamily from '../../../../assets/css/fonts';
import Text from '../../../../components/Typography/Text';
import Button from '../../../../components/Buttons/Button';
import CloseButton from '../../../../components/Buttons/CloseButton';
import {
  formatPbDateDash,
  formatPbDate,
  formatPbDateTimeWithMillis,
} from '../../../../lib/fmt';
import Table, { columnType } from '../../../../components/ClearingTable/Table';
import {
  CustomBodyRenderCurrencyLeft,
  CustomBodyRenderAlign,
  CustomBodyRenderQty,
  CustomBodyRenderBoolean,
} from '../../../../components/Table/CustomBodyRender';
import { getActivitySummary, listActivities } from '../../../../services/ActivityService';
import ActivityOtherFeesTable from './ActivityOtherFeesTable';
import IconButton from '../../../../components/Buttons/IconButton';
import ActivityReportDetails from '../Components/ActivityReportDetails';

const useStyles = makeStyles(() => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 1500,
    width: '100%',
    margin: '0 auto',
    float: 'clear',
  },
  modalHeader: {
    backgroundColor: 'white',
    padding: 25,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    display: 'flex',
    '& div:nth-child(1)': {
      flex: 1,
    },
    '& div:nth-child(2)': {
      flex: 'none',
      '& .MuiIconButton-root': {
        borderRadius: 5,
        padding: 10,
        '&:hover svg path': {
          fill: color.primary.main,
        },
      },
      '& svg:last-child': {
        width: 15,
        height: 15,
      },
    },
  },
  modalBody: {
    backgroundColor: 'white',
    padding: '0px 30px',
    maxHeight: 600,
    overflowY: 'auto',
    fontFamily: fontFamily.headers,
    color: color.default.main,
    '& p': {
      fontSize: 16,
      fontFamily: fontFamily.headers,
      fontWeight: 400,
      lineHeight: '26px',
      color: color.default.main,
      marginTop: 0,
    },
    '& hr': {
      margin: '30px 0px',
    },
  },
  modalFooter: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    backgroundColor: 'white',
    padding: 25,
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    position: 'relative',
    '& button': {
      width: 'auto',
    },
  },
  closeButton: {
    '& button': {
      marginTop: '-4px !important',
    },
  },
  paper: {
    position: 'absolute',
  },
}));

export default function ActivityDetailsModal({
  onClose: handleClose,
  open: isOpen,
  value,
  breakdownOtherFees,
  otherFeeColumns,
  dynamicColumns,
  manipulateDynamicData,
}) {
  const classes = useStyles();
  const [rows, setRows] = React.useState([]);
  const [pagination, setPagination] = React.useState({});
  const [modalData, setModalData] = React.useState({});

  const columns = [
    {
      name: 'trnsId',
      label: 'TRNS ID',
      options: {
        customBodyRender: CustomBodyRenderAlign,
        display: false,
      },
    },
    {
      name: 'reportActivityId',
      label: 'Activity ID',
      options: {
        display: false,
      },
    },
    {
      name: 'systemDate',
      label: 'System Date',
      type: columnType.date,
    },
    {
      name: 'correspondent',
      label: 'Correspondent',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'branch',
      label: 'Branch',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'accountNo',
      label: 'Account No',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'masterAccountNo',
      label: 'Master Account No',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'rep',
      label: 'Rep',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'accountName',
      label: 'Account Name',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      // Already converted to string in handlesearch
      name: 'tradeDate',
      label: 'Trade Date',
      type: columnType.date,
    },
    {
      // Already converted to string in handlesearch
      name: 'settleDate',
      label: 'Settle Date',
      type: columnType.date,
    },
    {
      name: 'entryType',
      label: 'Entry Type',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'symbol',
      label: 'Symbol',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'side',
      label: 'Side',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'qty',
      label: 'Qty',
      options: {
        customBodyRender: CustomBodyRenderQty,
      },
      addFooter: true,
      type: columnType.quantity,
    },
    {
      name: 'price',
      label: 'Price',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      type: columnType.amount,
    },
    {
      name: 'grossAmt',
      label: 'Gross Amount',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'regFee',
      label: 'Reg Fee',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'commission',
      label: 'Commission',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'tafFee',
      label: 'Taf Fee',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'netAmt',
      label: 'Net Amount',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'otherFees',
      label: 'Fee Details',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'otherFees2',
      label: 'Other Fees',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
      type: columnType.amount,
    },
    {
      name: 'description',
      label: 'Description',
    },
    {
      name: 'isArchived',
      label: 'Is Archived',
      options: {
        customBodyRender: CustomBodyRenderBoolean,
        filter: false,
        display: false,
      },
    },
    {
      name: 'a-selectAll',
      label: 'Select All',
      options: {
        display: true,
        print: false,
        download: false,
        filter: false,
        setCellHeaderProps: () => ({
          style: {
            width: '0px',
            display: 'table-cell',
            padding: 0,
            pointerEvents: 'none',
            fontSize: 0,
          },
        }),
      },
    },
  ];

  const options = {
    responsive: 'standard',
    selectableRows: 'none',
    serverSide: true,
  };

  useEffect(() => {
    async function init() {
      setModalData({
        ...modalData,
        searchType: 'Uncompress',
        dateType: 'System Date',
        fromDate: formatPbDateDash(value.systemDate),
        toDate: formatPbDateDash(value.systemDate),
        compressId: value.compressId,
        accountId: value.accountId,
        subAccountType: value.subAccountType,
        subAccountNo: value.subAccountNo,
        effSettleDate: formatPbDateDash(value.effSettleDate),
        capacity: value.capacity,
        effTradeDate: formatPbDateDash(value.effTradeDate),
        contraSubAccountNo: value.contraSubAccountNo,
        contraAccountId: value.contraAccountId,
        breakdownOtherFees: breakdownOtherFees,
      });
    }
    if (isOpen) {
      init();
    }
  }, [isOpen]);

  useEffect(() => {
    if (modalData && modalData.fromDate) {
      bindData();
    }
  }, [modalData]);

  const bindData = async (paginationChange) => {
    let paginationCopy = {
      ...pagination,
      reload: false,
      pageNo: paginationChange === true ? pagination.pageNo : 0,
      rowsPerPage: pagination.rowsPerPage || 100,
    };
    const data = await listActivities(modalData, paginationCopy);
    setRows(manipulateDynamicData(data));
    if (paginationChange !== true) {
      await getGridSummary(modalData, paginationCopy);
    }
  };

  const getGridSummary = async (searchData, paginationCopy) => {
    const data = await getActivitySummary(searchData, paginationCopy);

    paginationCopy.count = data.totalRows;
    setPagination(paginationCopy);
  };

  const getCsvData = async () => {
    if (rows.length === 0) {
      return [];
    }
    const data = await listActivities(modalData);
    const activitiesList = manipulateDynamicData(data);

    const rowsCopyOutput = activitiesList.map((data) => {
      let tempDate1 = formatPbDate(data.effSettleDate);
      data.effSettleDate = tempDate1;

      let tempDate2 = formatPbDate(data.effTradeDate);
      data.effTradeDate = tempDate2;

      let tempDate3 = formatPbDate(data.effAtlSettleDate);
      data.effAtlSettleDate = tempDate3;

      let tempDate4 = formatPbDate(data.effAtlTradeDate);
      data.effAtlTradeDate = tempDate4;

      let tradeAt = formatPbDateTimeWithMillis(data?.tradeAt);

      data.tradeAt = tradeAt;

      delete data.createdAt;

      return data;
    });

    return rowsCopyOutput;
  };

  const handleCloseReportDetails = () => {
    setModalProps({
      open: false,
      value: {},
    });
  };

  const [modal, setModalProps] = useModal(ActivityReportDetails, {
    open: false,
    value: {},
    onClose: () => handleCloseReportDetails,
  });

  const renderDetailsButton = (reportId, currency, isArchived) => {
    if (reportId) {
      return [
        <IconButton
          key={1}
          icon="receipt"
          text="View Details"
          color="inherit"
          onClick={() => {
            setModalProps({
              open: true,
              value: {
                id: reportId,
                currency: currency,
                archived: isArchived,
              },
            });
          }}
        />,
      ];
    }
    return [];
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={classes.modal}
      open={isOpen}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={isOpen}>
        <div style={{ width: 1500 }}>
          <div className={classes.modalHeader}>
            <div>
              <Text mt={3} variant="h2" label="Activity Table Details" />
            </div>
            <div className={classes.closeButton}>
              <CloseButton onClick={handleClose} />
            </div>
          </div>
          <div className={classes.modalBody}>
            {modalData.breakdownOtherFees && otherFeeColumns.length ? (
              <div>
                <ActivityOtherFeesTable
                  title={'ACTIVITY COMPRESSED DETAILS - Other Fees Breakdown'}
                  data={rows}
                  columns={[...columns, ...dynamicColumns]}
                  options={options}
                  onSearch={bindData}
                  pagination={pagination}
                  setPagination={setPagination}
                  getCsvData={getCsvData}
                  renderRowDetailsAddOnButtons={(row) => {
                    const reportId =
                      row.find((item) => item.label === 'Activity ID')?.value || 0;
                    const currency =
                      row.find((item) => item.label === 'Currency')?.value || '';
                    const archived =
                      row.find((item) => item.label === 'Is Archived')?.value || 0;
                    return renderDetailsButton(reportId, currency, archived);
                  }}
                />
              </div>
            ) : (
              <div>
                <Table
                  title={'ACTIVITY COMPRESSED DETAILS'}
                  data={rows}
                  columns={columns}
                  options={options}
                  onSearch={bindData}
                  pagination={pagination}
                  setPagination={setPagination}
                  getCsvData={getCsvData}
                  renderRowDetailsAddOnButtons={(row) => {
                    const reportId =
                      row.find((item) => item.label === 'Activity ID')?.value || 0;
                    const currency =
                      row.find((item) => item.label === 'Currency')?.value || '';
                    const archived =
                      row.find((item) => item.label === 'Is Archived')?.value || 0;
                    return renderDetailsButton(reportId, currency, archived);
                  }}
                />
              </div>
            )}
          </div>
          <div className={classes.modalFooter}>
            <Button label="Close" onClick={handleClose} />
          </div>
          {modal}
        </div>
      </Fade>
    </Modal>
  );
}
